import 'dayjs/locale/id'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import dateFormatter from 'helpers/formats/date'

dayjs.extend(utc)

const getDateTimeUTC = (date: any): string => {
  return dayjs(date).utc().format()
}

const isValidDate = (dateObject) => {
  return new Date(dateObject).toString() !== 'Invalid Date'
}

const isValidMapsURL = (value) => {
  if (!value) {
    return true
  }

  let urlPrefixes = [
    'https://www.google.com/maps/',
    'https://google.com/maps/',
    'https://goo.gl/maps/',
    'https://maps.google.com/maps/',
    'www.google.com/maps/',
    'google.com/maps/',
    'goo.gl/maps/',
    'maps.google.com/maps/',
    'https://maps.app.goo.gl/',
  ]

  return urlPrefixes.some((url) => value.startsWith(url))
}

const convertTZ = (date, tzString) => {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString(
      'en-US',
      { timeZone: tzString },
    ),
  )
}

const getDefaultStartDate = () => {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d
}

/**
 * Shown in place of a date when there is nothing to show.
 *
 * An en dash rather than an empty cell, so it reads as "nothing here" instead of
 * looking like a rendering failure.
 */
const NO_ACTIVITY_PLACEHOLDER = '-'

/**
 * Formats a team member's `last_activity_at` for the roster's "Terakhir Aktif"
 * column and the member detail dialog.
 *
 * Renders the date only. The column is 140px wide, so a full timestamp wraps to
 * two lines, and "when did this person last use the account" is a question that
 * days answer. Absolute rather than relative ("2 days ago"), which would need a
 * locale threaded through every caller to avoid emitting English.
 *
 * Delegates to {@link dateFormatter} rather than formatting here, so this column
 * renders like every other date in the app — including its locale resolution,
 * which reads i18nextLng from localStorage and mutates the global dayjs locale.
 * Worth knowing: that lookup returns the raw stored text, so a value written via
 * setLocalStorage comes back JSON-quoted, dayjs rejects it as an unknown locale,
 * and the previous locale sticks. Formatting is therefore only as locale-correct
 * as the rest of the app's dates — deliberately, so this one column cannot drift
 * from them.
 *
 * @param lastActivityAt - ISO 8601 timestamp from `GET /members`, or `null` for a
 *   `pending_activation` member who has never signed in.
 * @returns The formatted date, or {@link NO_ACTIVITY_PLACEHOLDER} when there is
 *   no activity or the value cannot be parsed. Never `"Invalid Date"`, which is
 *   what dayjs renders for unparseable input.
 *
 * @example
 * formatLastActive('2026-08-05T17:44:58+08:00') // => '05 Agt 2026' under id
 * formatLastActive(null)                        // => '-'
 * formatLastActive('nonsense')                  // => '-'
 */
const formatLastActive = (
  lastActivityAt?: string | null,
): string => {
  if (!lastActivityAt || !isValidDate(lastActivityAt)) {
    return NO_ACTIVITY_PLACEHOLDER
  }

  return dateFormatter({
    date: lastActivityAt,
    format: 'DD MMM YYYY',
  })
}

export {
  getDateTimeUTC,
  isValidDate,
  isValidMapsURL,
  convertTZ,
  getDefaultStartDate,
  formatLastActive,
  NO_ACTIVITY_PLACEHOLDER,
}
